import { OrderStopTimeTypeMap, OrderStopType, OrderStopTypeMap } from "@/constants/order";
import type { OrderDto, OrderStopDto } from "@/api/orders/dto/order.dto";
import { getCurrentInstance } from "vue";
import { useDateTime, useFilters } from "@/composables";
import type { TripOrder, TripOrderStop, TripRouteStop, TripStop } from "@/types/trip";
import { customAlphabet } from "nanoid";
import type { TripMonitorRouteStop } from "@/api/trips/dto/trip-monitor";
import i18 from "@/plugins/i18n";
import type { TravelOrderPayment, TripStopDto } from "@/api/trips/dto/trip.dto";

const t = i18.global.t;

export function useOrder() {
  const { getTZNames } = useDateTime();
  const { round } = useFilters();

  const STOP_DELIVERY = OrderStopType.delivery;
  const STOP_PICK_UP = OrderStopType.pickup;
  const STOP_RELOAD = OrderStopType.reload;

  const timeTypesMap: { [key: string]: string } = {
    ap: "Appointment",
    as: "ASAP",
    d: "Direct",
    f: "FCFS",
  };

  const certificates = ["Hazmat", "Tanker", "TSA", "TWIC"];

  const orderColorList = [
    "primary",
    "success",
    "warning",
    "info",
    "danger",
    "secondary",
    "light",
    "dark",
    "purple",
    "teal",
  ];

  function getOrderRoute(order: OrderDto | TripOrder): string {
    let firstPickup: OrderStopDto | TripOrderStop | null = null;
    let lastDelivery: OrderStopDto | TripOrderStop | null = null;

    const stops = order.orderStops;

    for (const stop of stops) {
      if (stop.type === STOP_PICK_UP && firstPickup === null) {
        firstPickup = stop;
      }
      if (stop.type === STOP_DELIVERY) {
        lastDelivery = stop;
      }
    }

    return `${firstPickup?.facility?.address} → ${lastDelivery?.facility?.address}`;
  }

  function getStopDistance(
    stops: OrderStopDto[],
    distances: {
      [key: string]: number;
    },
    orderIndex: number,
  ): string {
    const distanceKey = `${stops[orderIndex].id}-${stops[orderIndex + 1].id}`;
    const distanceInMiles = distances[distanceKey] || 0;
    return getCurrentInstance()?.appContext.config.globalProperties.$filters.miles(distanceInMiles) || "";
  }

  function getStopTime(
    stop: OrderStopDto | TripMonitorRouteStop | TripStop | TripRouteStop,
    { showFirstTime = true, showSecondTime = true } = {},
  ): string {
    const times = [];
    if (showFirstTime && stop.timeTo) {
      times.push(getTimeInterval(stop.timeFrom!, stop.timeTo, stop.timezone));
    }
    if (showSecondTime && "time2To" in stop && "time2From" in stop && stop.time2To) {
      times.push(getTimeInterval(stop.time2From, stop.time2To, stop.timezone));
    }
    return times.join(", ");
  }

  function getTimeType(id: string) {
    return OrderStopTimeTypeMap.find((t) => t.id === id)?.name;
  }

  function getTimeInterval(from: string | undefined | null, to: string | undefined, timezone: string | null): string {
    const tzShort = getTZNames(timezone);
    const intervals = [];

    if (from) intervals.push(getDateToDisplay(from, timezone));
    if (to) intervals.push(getDateToDisplay(to, timezone));
    if (intervals.length === 0) return "";
    if (intervals.length === 2) {
      if (intervals[0].date === intervals[1].date) {
        return `${intervals[0].date} ${intervals[0].time} - ${intervals[1].time} ${tzShort}`;
      }
    }

    return intervals.map((d) => `${d.date} ${d.time}`).join(" - ") + ` ${tzShort}`;
  }

  function getDateToDisplay(date: string, timezone: string | null) {
    const dayjs = getCurrentInstance()?.appContext.config.globalProperties.$dayjs;
    const dateTz = dayjs(date).tz(timezone || undefined);
    return {
      date: dateTz.format("MM/DD/YYYY"),
      time: dateTz.format("HH:mm"),
    };
  }

  function getTimeTypes(type: OrderStopType) {
    const keys = Object.keys(timeTypesMap);
    return (type === OrderStopType.pickup ? keys.filter((t) => t !== "d") : keys.filter((t) => t !== "as")).map(
      (k) => ({
        id: k,
        name: timeTypesMap[k],
      }),
    );
  }

  function isReloadStop(stop: TripStop | TripRouteStop | TripStopDto) {
    return Boolean(stop.isReload);
  }

  function generateId() {
    const nanoid = customAlphabet("1234567890abcdef", 16);
    return nanoid();
  }

  function createStopName(
    type: OrderStopType,
    number: number | null,
    options: {
      suffix?: string;
      prefix?: string;
    } = {},
  ): string {
    const { prefix = "", suffix = "" } = options;

    return `${prefix} ${OrderStopTypeMap[type]} ${suffix} #${number}`;
  }

  function createTOName(index: number, orderNumber: number | undefined, prefix = t("general.travel-order")) {
    return `${prefix} #${index + 1} (${t("general.order")}#${orderNumber})`;
  }

  function getTravelOrderPaymentsAmount(payments: TravelOrderPayment[]) {
    return payments.reduce((acc, payment) => {
      acc += Number(payment.amount);
      return round(acc);
    }, 0);
  }

  return {
    STOP_DELIVERY,
    STOP_PICK_UP,
    STOP_RELOAD,
    timeTypesMap,
    certificates,
    getOrderRoute,
    getStopDistance,
    getStopTime,
    getTimeType,
    getTimeInterval,
    getDateToDisplay,
    getTimeTypes,
    isReloadStop,
    generateId,
    createStopName,
    createTOName,
    getTravelOrderPaymentsAmount,
    orderColorList,
  };
}
